import { useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { FolderNameLink } from 'ui/indexPages/folders/Folders.styles';

import { createWorkflowFolderMutation } from 'api/mutations/workflowIndex';

import { AncestorIdData, AncestorNameData } from 'modules/domains/foldering';

import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { NamedFolder, ResponseWrapper } from 'ui/toast';

import { QUERY_PARAMS } from '../constants';
import { formatFolderName } from '../utils/formatFolderName';

const displayText = {
  genericError: 'Could not create folder',
};

export const useCreateWorkflowFolder = () => {
  const dispatch = useDispatch();
  const [createWorkflowFolder] = useMutation(createWorkflowFolderMutation);

  return useCallback(
    async (newFolderName: string, newFolderDestination: AncestorIdData & AncestorNameData) => {
      try {
        const { data: creationMutationData, errors: creationMutationErrors } =
          await createWorkflowFolder({
            variables: {
              name: newFolderName,
              locationId: newFolderDestination.id,
            },
          });
        if (!creationMutationData?.createWorkflowFolder || creationMutationErrors) {
          dispatch(addNegativeToastWithErrorId(displayText.genericError, 'createWorkflowFolder'));
          return;
        }

        const newFolderId = creationMutationData.createWorkflowFolder;
        const { location } = window;
        const urlParams = new URLSearchParams(location.search);
        urlParams.set(QUERY_PARAMS.FOLDER_ID, newFolderId.toString());

        dispatch(
          addPositiveToast(
            <ResponseWrapper key="toast-success">
              <FolderNameLink to={`${location.pathname}?${urlParams.toString()}`}>
                <NamedFolder name={newFolderName} />
              </FolderNameLink>{' '}
              was successfully created
              {newFolderDestination.name ? (
                <span>
                  {' '}
                  in the <NamedFolder name={formatFolderName(newFolderDestination.name)} /> folder.
                </span>
              ) : (
                '.'
              )}
            </ResponseWrapper>,
          ),
        );
      } catch (error) {
        if (error instanceof Error && error.message.includes('maintenance')) {
          dispatch(addNegativeToastWithErrorId(error.message, 'createWorkflowFolder'));
        } else {
          dispatch(addNegativeToastWithErrorId(displayText.genericError, 'createWorkflowFolder'));
        }
      }
    },
    [createWorkflowFolder],
  );
};
